import type { Country, CountryPolygonsTopology } from '../data';
import type { Bbox } from '../geo/types';
import { decodeCountryPolygons, type PolygonWithHoles } from '../borders';

/**
 * Country hit-tester. Given a (lat, lon) on the sphere, returns the Country
 * that contains it — or null for ocean / unassigned region.
 *
 * Strategy:
 *   1. Bbox pre-filter — ~245 cheap comparisons knock out most candidates.
 *   2. For each survivor, 2D point-in-polygon (ray casting) against each
 *      sub-polygon's outer ring.
 *   3. If a hit lands on an outer ring, also check the polygon's holes —
 *      a point inside a hole is NOT inside that country (so clicking
 *      Lesotho doesn't resolve to South Africa, Vatican to Italy, etc).
 *   4. Returns first positive match.
 *
 * Known limitations (Phase 1 acceptable; spherical PIP is a follow-up):
 *   - 2D ray-cast test is run in equirectangular space. Polygons that cross
 *     the antimeridian (Russia's easternmost region wrapping past ±180°)
 *     or surround a pole (Antarctica) can return wrong results near those
 *     singularities.
 *   - Bbox of a MultiPolygon with scattered territories is unnecessarily
 *     wide (France's bbox includes the Pacific because of Clipperton),
 *     leading to some wasted PIP calls. Still fast enough at 245
 *     countries to not bother optimizing yet.
 */
export class CountryHitTester {
  private readonly entries: Array<{
    iso: string;
    meta: Country;
    bbox: Bbox;
    polygons: PolygonWithHoles[];
  }>;

  constructor(polygonsDataset: CountryPolygonsTopology, countries: Country[]) {
    const metaByIso = new Map(countries.map((c) => [c.iso_a2, c]));
    const groups = decodeCountryPolygons(polygonsDataset);
    const entries: typeof this.entries = [];
    for (const group of groups) {
      const meta = metaByIso.get(group.iso);
      if (!meta) continue;
      entries.push({ iso: group.iso, meta, bbox: meta.bbox, polygons: group.polygons });
    }
    this.entries = entries;
  }

  /** Return the country containing the point, or null if none. */
  find(lat: number, lon: number): Country | null {
    for (const entry of this.entries) {
      const [minLon, minLat, maxLon, maxLat] = entry.bbox;
      if (lon < minLon || lon > maxLon || lat < minLat || lat > maxLat) continue;
      for (const poly of entry.polygons) {
        if (!pointInRing(lon, lat, poly.outer)) continue;
        // Inside outer — check holes. A point inside any hole means the
        // point belongs to whatever country fills that hole, not this one.
        let inHole = false;
        for (const hole of poly.holes) {
          if (pointInRing(lon, lat, hole)) {
            inHole = true;
            break;
          }
        }
        if (!inHole) return entry.meta;
      }
    }
    return null;
  }

  /** Number of countries indexed — informational. */
  size(): number {
    return this.entries.length;
  }
}

/**
 * Classic 2D ray-cast PIP. Cast a horizontal ray east from the point; count
 * ring edges it crosses. Odd = inside, even = outside. Runs in equirectangular
 * space — limitations noted on the class doc.
 */
function pointInRing(lon: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  const n = ring.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    const intersect =
      yi > lat !== yj > lat && lon < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
